/**
 * 가격표 카드 컴포넌트
 */
import React from 'react';
import styled from 'styled-components';
import { Button } from '../../atoms/Button';
import { tokens } from '../../../design-system/tokens';

interface PricingCardProps {
  title: string;
  price: number;
  description: string;
  features: readonly string[];
  productType: string;
  paymentType: 'oneTime' | 'subscription';
  recommended?: boolean;
  onPurchase: () => void;
}

const Card = styled.div<{ $recommended?: boolean }>`
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.lg};
  padding: ${tokens.spacing[8]};
  box-shadow: ${({ $recommended }) =>
    $recommended ? tokens.shadows.lg : tokens.shadows.base};
  position: relative;
  border: ${({ $recommended }) =>
    $recommended ? `2px solid ${tokens.colors.primary[500]}` : `1px solid ${tokens.colors.neutral[200]}`};
  transition: transform 0.2s, box-shadow 0.2s;
  word-break: keep-all;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${tokens.shadows.lg};
  }

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[6]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[5]};
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -12px;
  right: ${tokens.spacing[5]};
  background: ${tokens.colors.primary[500]};
  color: ${tokens.colors.neutral[0]};
  padding: ${tokens.spacing[1]} ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.semibold};

  @media (max-width: ${tokens.breakpoints.sm}) {
    right: ${tokens.spacing[4]};
    padding: ${tokens.spacing[1]} ${tokens.spacing[3]};
  }
`;

const Title = styled.h3`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.lg};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.base};
  }
`;

const Description = styled.p`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[5]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
    margin-bottom: ${tokens.spacing[4]};
  }
`;

const Price = styled.div`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.primary[500]};
  margin-bottom: ${tokens.spacing[6]};

  span {
    font-size: ${tokens.typography.fontSize.base};
    color: ${tokens.colors.neutral[600]};
    font-weight: ${tokens.typography.fontWeight.regular};
  }

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['2xl']};
    margin-bottom: ${tokens.spacing[5]};

    span {
      font-size: ${tokens.typography.fontSize.sm};
    }
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xl};
    margin-bottom: ${tokens.spacing[4]};

    span {
      font-size: ${tokens.typography.fontSize.xs};
    }
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${tokens.spacing[6]} 0;

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[4]};
  }
`;

const FeatureItem = styled.li`
  padding: ${tokens.spacing[2]} 0;
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  display: flex;
  align-items: center;
  word-break: keep-all;

  &:before {
    content: '✓';
    color: ${tokens.colors.primary[500]};
    font-weight: bold;
    margin-right: ${tokens.spacing[3]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
    padding: ${tokens.spacing[1]} 0;
  }
`;

export const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  description,
  features,
  recommended,
  onPurchase
}) => {
  return (
    <Card $recommended={recommended}>
      {recommended && <RecommendedBadge>추천</RecommendedBadge>}

      <Title>{title}</Title>
      <Description>{description}</Description>

      <Price>
        ₩{price.toLocaleString()}
        <span>원</span>
      </Price>

      <FeatureList>
        {features.map((feature, index) => (
          <FeatureItem key={index}>{feature}</FeatureItem>
        ))}
      </FeatureList>

      <Button onClick={onPurchase} fullWidth>
        구매하기
      </Button>
    </Card>
  );
};